import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { BacteriaIcon, BugIcon, FishIcon, LeafIcon, MushroomIcon } from './icons/TaxaIcons.tsx'
import type { Taxas } from 'Model/ITaxa.js'

/**
 * The taxonomy glyphs come from ./icons/TaxaIcons, vendored because heroicons carries 325 general UI
 * glyphs and no organism at all.
 *
 * Matches on the lower-cased value, so every case must be written lower-case: the previous
 * `case 'Fungi'` and friends could never match and `custom_primer` had no case at all, which rendered
 * no icon rather than falling back.
 */
/**
 * react-icons rendered these at `1em`, so the call sites size them with a text class (`text-xl`) or
 * pass nothing and inherit. Neither an inline heroicon nor a vendored glyph declares width/height, so
 * without this they lay out at the SVG default — which is how dropping react-icons made them huge.
 *
 * A caller that names its own width/height keeps it; this only supplies the missing default.
 */
const withFallbackSize = (classNames: string): string =>
  /(^|\s)(w-|h-|size-)/.test(classNames) ? classNames : `w-[1em] h-[1em] ${classNames}`.trim()

export function getIconFromTaxa(targetTaxa: string | Taxas | null, classNames: string = '') {
  const className = withFallbackSize(classNames)
  switch (targetTaxa?.toLowerCase()) {
    case 'fungi':
    case 'fits2_fungal':
      return <MushroomIcon className={className} />
    case 'bacteria':
    case '_16s_bacteria':
      return <BacteriaIcon className={className} />
    case 'animal':
    case '16s_vertebrates':
    case 'co1_animal':
      return <BugIcon className={className} />
    case '12s_fish':
      return <FishIcon className={className} />
    case 'plant':
    case 'pits2_plant':
      return <LeafIcon className={className} />
    default:
      return <QuestionMarkCircleIcon className={className} />
  }
}
